import { useEffect, useState } from 'react';
import { Button, Group, Stack, TextInput } from '@mantine/core';
import type {
  AddressBookCreateInput,
  AddressBookEntryDto,
  AddressBookPatchInput,
} from '@expeditor/shared';
import { addressBookApi } from '../../shared/api/reference-books';
import { ReferenceBookTable } from './ReferenceBookTable';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';

export function AddressBookPage() {
  return (
    <ReferenceBookTable<AddressBookEntryDto, AddressBookCreateInput, AddressBookPatchInput>
      title="Мои адреса"
      api={addressBookApi}
      columns={[
        { header: 'Название', render: (i) => i.label },
        { header: 'Страна', render: (i) => i.country ?? '—' },
        { header: 'Адрес', render: (i) => i.address },
        { header: 'Контакт', render: (i) => i.contactName ?? '—' },
        { header: 'Телефон', render: (i) => i.contactPhone ?? '—' },
        { header: 'Погрузка', render: (i) => i.loadingType ?? '—' },
      ]}
      renderForm={AddressForm}
    />
  );
}

function AddressForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial: AddressBookEntryDto | null;
  onSubmit: (dto: AddressBookCreateInput | AddressBookPatchInput) => Promise<void>;
  submitting: boolean;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [country, setCountry] = useState(initial?.country ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [contactName, setContactName] = useState(initial?.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  const [loadingType, setLoadingType] = useState(initial?.loadingType ?? '');
  // Разбивка адреса (для ЭТрН) + телефон для ЭПД — одна на текст адреса.
  const { parts, setParts, save: saveParts } = useAddressBreakdown(initial?.address);

  useEffect(() => {
    setLabel(initial?.label ?? '');
    setCountry(initial?.country ?? '');
    setAddress(initial?.address ?? '');
    setContactName(initial?.contactName ?? '');
    setContactPhone(initial?.contactPhone ?? '');
    setLoadingType(initial?.loadingType ?? '');
  }, [initial?.id]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Разбивка живёт отдельно (одна на текст адреса) — сохраняем рядом с записью.
    void saveParts(address).catch(() => undefined);
    void onSubmit({
      label,
      country: country || undefined,
      address,
      contactName: contactName || undefined,
      contactPhone: contactPhone || undefined,
      loadingType: loadingType || undefined,
    });
  };

  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Название"
          required
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
          placeholder="Главный склад"
        />
        <Group grow>
          <TextInput
            label="Страна"
            placeholder="Россия"
            value={country}
            onChange={(e) => setCountry(e.currentTarget.value)}
          />
          <TextInput
            label="Адрес"
            description="Без страны — она в отдельном поле"
            required
            value={address}
            onChange={(e) => setAddress(e.currentTarget.value)}
          />
        </Group>
        <AddressBreakdownFields value={parts} onChange={setParts} />
        <TextInput
          label="Контактное лицо"
          value={contactName}
          onChange={(e) => setContactName(e.currentTarget.value)}
        />
        <TextInput
          label="Телефон"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.currentTarget.value)}
        />
        <TextInput
          label="Тип погрузки"
          placeholder="задняя / боковая / верхняя"
          value={loadingType}
          onChange={(e) => setLoadingType(e.currentTarget.value)}
        />
        <Button type="submit" loading={submitting}>
          Сохранить
        </Button>
      </Stack>
    </form>
  );
}
